import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../../../hooks/use-user-context';
import { rem } from '../../../utilities/style';
import { createOptionMap } from '../../../utilities/misc';
import { colors } from '../../../theme/colors';
import { fontWeights } from '../../../theme/type';

import { ReactComponent as IconArrowRight } from '../../../images/icons/arrow-right.svg';
import Link from '../../Link';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  background-color: ${colors.blue['500']};
  border-radius: 10px;
  padding: ${rem(32)};
  color: ${colors.white};
  height: 100%;
`;

const HeadLine = styled.h2`
  font-size: ${rem(14)};
  margin-bottom: ${rem(20)};
  font-weight: ${fontWeights.bold};
  text-transform: uppercase;
`;

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  margin-bottom: ${rem(12)};
  font-size: ${rem(16)};
  color: ${colors.white};
  font-weight: ${fontWeights.medium};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ArrowStyled = styled(IconArrowRight)`
  margin-left: ${rem(16)};
  transform: translateX(${rem(-8)});
  transition: all .2s ease;
  opacity: 0;

  ${LinkStyled}:hover & {
    opacity: 1;
    transform: none;
  }
`;

const FeaturedResourcesLinks = ({ links }) => {
  const data = useStaticQuery(graphql`
    query FeaturedResourcesLinks {
      allContentfulOptionSet(filter: {name: {eq: "featured_resources"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  return (
    <Container>
      <HeadLine>
        <TextRenderer
          text={options['featured_resources.more_resources_title']}
        />
      </HeadLine>
      {links.map(({ id, headline, link }) => (
        <LinkStyled key={id} to={link}>
          <TextRenderer text={headline} />
          <ArrowStyled />
        </LinkStyled>
      ))}
    </Container>
  );
};

FeaturedResourcesLinks.propTypes = {
  links: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired
  }))
};

export default FeaturedResourcesLinks;
